import * as XLSX from "xlsx";
import type { BomFlowNode, BomFlowEdge } from "../../contexts/types";

export interface BomExportRow {
  level: number;
  modelNo: string;
  processCode: string;
  itemNo: string;
  itemName: string;
}

/**
 * 出次数0（=矢印の終点にしかならないノード。最終組立を想定）をLevel0とし、
 * そこから矢印を逆方向にBFSで辿るごとに+1する。
 * アスタリスク個数方式（最終組立=アスタリスクなし=Lv0）に合わせた向き。
 * 循環がある場合は呼び出し側で detectCycle（04）により事前にブロックされている前提だが、
 * 防御的に guard で無限ループを止める。
 */
export function computeLevels(nodes: BomFlowNode[], edges: BomFlowEdge[]): Record<string, number> {
  // ノードから出ていく矢印の数をカウント。
  const outdeg: Record<string, number> = {};
  // 行先から見てどのノードに接続されているかの関係リスト。
  const parents: Record<string, string[]> = {};

  // 全ノードの初期化
  nodes.forEach((n) => { outdeg[n.id] = 0; parents[n.id] = []; });
  // 矢印を見て、数字を増やしたりリストに追加する。
  edges.forEach((e) => {
    if (!(e.source in outdeg) || !(e.target in parents)) return;
    outdeg[e.source] = (outdeg[e.source] ?? 0) + 1;
    parents[e.target].push(e.source);
  });

  const level: Record<string, number> = {};
  const queue: string[] = [];
  nodes.forEach((n) => {
    if (outdeg[n.id] === 0) {
      level[n.id] = 0;
      queue.push(n.id);
    }
  });

  let guard = 0;
  while (queue.length && guard++ < 2000) {
    const id = queue.shift()!;
    (parents[id] ?? []).forEach((p) => {
      const next = level[id] + 1;
      if (level[p] === undefined || next > level[p]) {
        level[p] = next;
        queue.push(p);
      }
    });
  }
  // 孤立ノード（矢印が一本もない）は出次数0として扱われ、この時点でLevel0が入っているはず。
  // 念のためのフォールバック。
  nodes.forEach((n) => { if (level[n.id] === undefined) level[n.id] = 0; });

  return level;
}

/**
 * BOM（部品構成表）の各行データを、製造手順に沿った波型（深さ優先ツリー構造）の並び順で生成する。
 * @param nodes ReactFlow上の全工程（ノード）データ配列
 * @param edges 工程間の親子関係を表す矢印（エッジ）データ配列
 * @returns 製造順（波型構造）に並び替えられたBOM出力用の行データ配列
 */
export function computeExportRows(nodes: BomFlowNode[], edges: BomFlowEdge[]): BomExportRow[] {
  const levelById = computeLevels(nodes, edges);
  const nodeById = Object.fromEntries(nodes.map((n) => [n.id, n]));

  // childrenOf[親.id] = [子(下位部品/工程).id]。computeLevelsの parents マップと同じ辺の向き（source=子, target=親）を使う。
  const childrenOf: Record<string, string[]> = {};
  edges.forEach((e) => { (childrenOf[e.target] ??= []).push(e.source); });

  const rows: BomExportRow[] = [];
  const visited = new Set<string>(); // 循環がある場合の防御（04でブロックされる前提だが念のため）
  function visit(id: string) {
    if (visited.has(id)) return;
    visited.add(id);
    const n = nodeById[id];
    const lv = levelById[id];
    // 1. 工程自身の出力（行き掛け処理：親工程をまず記録）
    rows.push({ level: lv, modelNo: n.data.modelNo, processCode: n.data.processCode, itemNo: n.data.itemNo, itemName: n.data.itemName });

    // 2. 子工程へ再帰ダイブ（深さ優先）
    // 下位の工程・部品を最深部まで先に出力させるため、この工程の材料出力は一時待機（コールスタックに積む）
    (childrenOf[id] ?? [])
      .map((cid) => nodeById[cid])
      .filter(Boolean)
      .sort((a, b) => a.position.y - b.position.y)
      .forEach((c) => visit(c.id));

    // 3. 自工程の材料の出力（帰り掛け処理）
    // 下位の組み立てがすべて完了した（戻ってきた）タイミングで、この工程に投入する材料を出力
    n.data.materials.forEach((m) => {
      rows.push({ level: lv + 1, modelNo: '', processCode: '', itemNo: m.itemNo, itemName: m.itemName });
    });


  }

  // ルート（Level0）が複数ある場合（課題4、07§1で解決済み）もY座標昇順で順に辿る
  nodes
    .filter((n) => levelById[n.id] === 0)
    .sort((a, b) => a.position.y - b.position.y)
    .forEach((r) => visit(r.id));

  return rows;
}

/** levelをアスタリスク表記に変更 */
export function levelToAsterisk(level: number): string {
  if (level <= 0) return '-';

  // 数値を文字にして2桁にパディングする。
  const formattedLevel = String(level).padStart(2, '0');

  return '*'.repeat(level) + formattedLevel;
}

export const EXPORT_HEADER = ['Seq.', 'Level', 'Model No.', 'Process Code', 'Item No.', 'Item Name'];

/**
 * Excel出力用のAOA(Array of Array)を生成する。
 * 1行目がヘッダ、以降はcomputeExportRowsの並び順にSeq(1始まり連番)を付与した本体行。
 */
export function buildBomExportAoa(nodes: BomFlowNode[], edges: BomFlowEdge[]): (string | number)[][] {
  const rows = computeExportRows(nodes, edges);
  const body = rows.map((r, i) => [
    i + 1,
    levelToAsterisk(r.level),
    r.modelNo,
    r.processCode,
    r.itemNo,
    r.itemName,
  ]);
  return [EXPORT_HEADER, ...body];
}

/**
 * BOM作成モーダルの内容をxlsxファイルとしてダウンロードする。
 * 呼び出し元で循環参照検知・Process Code重複チェックを済ませてから呼ぶこと。
 */
export function downloadBomExcel(nodes: BomFlowNode[], edges: BomFlowEdge[]) {
  const aoa = buildBomExportAoa(nodes, edges);
  const worksheet = XLSX.utils.aoa_to_sheet(aoa);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'ProcessBOM');

  const dateStr = new Date().toISOString().slice(0, 10).replace(/-/g, '');
  XLSX.writeFile(workbook, `ProcessBOM_${dateStr}.xlsx`);
}

/**
 * DFS + 訪問中(グレー)集合方式で循環を検知する。
 * 訪問中のノードへ再度到達する辺(後退辺)を検出したら循環と判定し、
 * 循環を構成するノードid配列を返す(なければnull)。
 */
export function detectCycle(nodes: BomFlowNode[], edges: BomFlowEdge[]): string[] | null {
  const state: Record<string, 0 | 1 | 2> = {}; // 0=未訪問, 1=訪問中, 2=完了
  const children: Record<string, string[]> = {};
  nodes.forEach((n) => { children[n.id] = []; });
  edges.forEach((e) => {
    if (children[e.source]) children[e.source].push(e.target);
  });

  const stack: string[] = [];
  let cyclePath: string[] | null = null;

  function dfs(id: string) {
    if (cyclePath) return;
    state[id] = 1;
    stack.push(id);
    (children[id] ?? []).forEach((next) => {
      if (cyclePath) return;
      if (state[next] === 1) {
        const idx = stack.indexOf(next);
        cyclePath = stack.slice(idx).concat(next);
        return;
      }
      if (state[next] !== 2) dfs(next);
    });
    stack.pop();
    state[id] = 2;
  }

  nodes.forEach((n) => { if (!state[n.id] && !cyclePath) dfs(n.id); });
  return cyclePath;
}

/**
 * 同一モーダル内でProcess Codeが重複しているノードを検出する。
 * (既存工程マスタとの重複チェックは行わない。)
 * 警告文言はi18n(t())が必要なため、UIに依存しない本関数では生成しない。
 * 呼び出し元（コンポーネント側）でこの真偽値を見て、t('features.InvestmentScreening.BomCreateModal.processCodeDuplicate')等の文言を表示すること。
 * @returns ノードid → 重複しているか のマップ（重複なしのノードはキーに含まれない）
 */
export function findProcessCodeConflicts(nodes: BomFlowNode[]): Record<string, boolean> {
  const byCode: Record<string, string[]> = {};
  nodes.forEach((n) => {
    const code = n.data.processCode;
    if (!code) return;
    (byCode[code] ??= []).push(n.id);
  });

  const conflictIds: Record<string, boolean> = {};
  nodes.forEach((n) => {
    const code = n.data.processCode;
    if (!code) return;
    if (byCode[code].length > 1) {
      conflictIds[n.id] = true;
    }
  });
  return conflictIds;
}